import React, { useEffect, useMemo, useRef } from "react"
import Alert from "react-bootstrap/Alert"
import { isContactSectionComplete } from "./sectionCompletion.js"
import { User } from "lucide-react"
import Accordion from "./Accordion.jsx"
import InputField from "./InputField.jsx"
import CustomerSearchInput from "./CustomerSearchInput.jsx"
import LicensePhotosUpload from "./LicensePhotosUpload.jsx"
import CustomerChoicePrompt from "./CustomerChoicePrompt.jsx"
import { FieldChangeHint } from "./FieldChangeHint.jsx"
import { OrderFormCheckbox, OrderFormRadio } from "./EnterKeyControls.jsx"
import { mapApiContactMethod } from "./orderPayload.js"
import { ContactService } from "../services/contactService.js"
import { ORDER_FORM_PROGRAMMATIC_SCROLL_ENABLED } from "./orderFormAccordionConstants.js"
import { focusElement } from "../utils/scrollFocusedElement.js"
import { NAME_COMMA_ERROR, nameHasComma, stripCommasFromName } from "./nameCommaValidation.js"
import {
    contactHasAnyDetail,
    contactHasEdits,
    getContactFieldDiffs,
    snapshotContactBaseline,
    undoContactField,
} from "./customerFormBaseline.js"

/**
 * UI reference: SportOMSBootv2 `src/components/forms/ContactForm.tsx`
 * (workspace sibling folder — not the StJeansOMS repo).
 */

function customerDisplayLabel(first, last) {
    return [first, last].filter(Boolean).join(" ").trim()
}

/** Matches `contact` initial state in NewOrderFormPage when no customer is chosen. */
function clearedContactState() {
    return {
        customerId: null,
        firstName: "",
        lastName: "",
        email: "",
        companyName: "",
        contact_method: "email",
        phone: "",
        is_staff: false,
        tripCode: "",
        fishTag: "",
        licenseNumber: "",
        prepaidAmount: "0",
        licenseFile: null,
        licenseFileFront: null,
        licenseFileBack: null,
        licenseFrontImg: null,
        licenseBackImg: null,
        ship_to: null,
        default_ship_id: null,
        bill_to: null,
        default_bill_id: null,
        bill_same_as_ship: false,
        updateCustomerFields: false,
        contactBaseline: null,
        selectedCustomerLabel: null,
    }
}

function tContact(v) {
    return String(v ?? "").trim()
}

function formatPhone(value) {
    return ContactService.formatPhone(value)
}

/** @param {Record<string, unknown>} v */
function firstMissingContactFieldId(v) {
    if (tContact(v.firstName) === "") {
        return "new-order-contact-first-name"
    }
    if (tContact(v.lastName) === "") {
        return "new-order-contact-last-name"
    }
    if (tContact(v.email) === "") {
        return "new-order-contact-email"
    }
    if (tContact(v.phone) === "") {
        return "new-order-contact-phone"
    }
    if (tContact(v.licenseNumber) === "") {
        return "new-order-contact-license"
    }
    return null
}

/**
 * @param {{
 *   value: Record<string, unknown>,
 *   onChange: (v: Record<string, unknown>) => void,
 *   onNext: () => void,
 *   title?: string,
 *   isOpen?: boolean,
 *   touched?: boolean,
 *   statusLabel?: string | null,
 *   disabled?: boolean,
 *   onHeaderClick?: () => void,
 *   isActive?: boolean,
 *   activationFocusMode?: "first" | "nextRequired" | null,
 *   onActivationFocusConsumed?: () => void,
 *   activationFocusDelayMs?: number,
 *   suppressProgrammaticNextFocus?: boolean,
 *   onSuppressProgrammaticNextFocusConsumed?: () => void,
 *   enablePrepaidAmount?: boolean,
 *   fieldErrors?: Record<string, string> | null,
 *   showFieldErrors?: boolean,
 *   hasErrors?: boolean,
 * }} props
 */
export default function ContactSection({
    value,
    onChange,
    onNext,
    title = "1. Contact Information",
    isOpen,
    touched = false,
    statusLabel = null,
    disabled = false,
    onHeaderClick,
    isActive = false,
    activationFocusMode = null,
    onActivationFocusConsumed,
    activationFocusDelayMs = 350,
    suppressProgrammaticNextFocus = false,
    onSuppressProgrammaticNextFocusConsumed,
    enablePrepaidAmount = true,
    fieldErrors = null,
    showFieldErrors = false,
    hasErrors = false,
}) {
    const nextButtonRef = useRef(/** @type {HTMLButtonElement | null} */(null))
    /** Section root for delayed inset scroll after customer select (same pattern as page). */
    const sectionRootRef = useRef(/** @type {HTMLDivElement | null} */(null))
    const valueRef = useRef(value)
    valueRef.current = value
    const prevCustomerIdRef = useRef(value.customerId ?? null)

    const isCompleted = isContactSectionComplete(value) && Boolean(touched)

    const baseline = value.contactBaseline ?? null
    const fieldDiffs = useMemo(
        () => (value.customerId != null ? getContactFieldDiffs(value, baseline) : []),
        [value, baseline]
    )
    const diffsByKey = useMemo(() => {
        /** @type {Record<string, (typeof fieldDiffs)[number]>} */
        const map = {}
        for (const d of fieldDiffs) {
            map[d.key] = d
        }
        return map
    }, [fieldDiffs])
    const showCreateCustomerAlert =
        value.customerId == null && contactHasAnyDetail(value)
    const showUpdateCustomerPrompt =
        value.customerId != null && fieldDiffs.length > 0

    function focusNextButton() {
        window.requestAnimationFrame(() => {
            const btn = nextButtonRef.current
            const root = sectionRootRef.current
            if (!btn || !root) {
                return
            }
            focusElement(btn, { enabled: ORDER_FORM_PROGRAMMATIC_SCROLL_ENABLED, delay: true })
        })
    }

    function selectCustomer(c) {
        console.log("Order form customer selected (full object):", c)
        const firstName = stripCommasFromName(c.first_name ?? "")
        const lastName = stripCommasFromName(c.last_name ?? "")
        const next = {
            ...value,
            customerId: c.id ?? null,
            firstName,
            lastName,
            email: c.email ?? "",
            phone: formatPhone(c.phone ?? ""),
            companyName: c.company ?? "",
            contact_method: mapApiContactMethod(c.contact_method),
            is_staff: Boolean(c.is_staff),
            tripCode: c.trip_code ?? "",
            fishTag: c.fish_tag ?? "",
            licenseNumber: c.fishing_license ?? value.licenseNumber ?? "",
            licenseFrontImg: c.license_front_img ?? null,
            licenseBackImg: c.license_back_img ?? null,
            licenseFile: null,
            licenseFileFront: null,
            licenseFileBack: null,
            ship_to: c.ship_to ?? null,
            default_ship_id: c.default_ship_id ?? null,
            bill_to: c.bill_to ?? null,
            default_bill_id: c.default_bill_id ?? null,
            bill_same_as_ship: Boolean(c.bill_same_as_ship),
            updateCustomerFields: false,
            selectedCustomerLabel: customerDisplayLabel(firstName, lastName) || null,
        }
        next.contactBaseline = snapshotContactBaseline(next)
        onChange(next)
        focusNextButton()
    }

    function patchContact(patch) {
        onChange({ ...value, ...patch })
    }

    function undoField(key) {
        const next = undoContactField(value, baseline, key)
        if (!contactHasEdits(next, baseline)) {
            next.updateCustomerFields = false
        }
        onChange(next)
    }

    function labelHint(key) {
        const d = diffsByKey[key]
        if (!d) {
            return null
        }
        return (
            <FieldChangeHint
                fromDisplay={d.fromDisplay}
                kind={d.kind}
                onUndo={() => undoField(key)}
            />
        )
    }

    useEffect(() => {
        const prevId = prevCustomerIdRef.current
        const nextId = value.customerId ?? null
        prevCustomerIdRef.current = nextId

        // If the customer was set programmatically (e.g. from Customers "+ Order"),
        // also give focus to the Next button to match the normal "selectCustomer" flow.
        if (!isActive || prevId != null || nextId == null) {
            return
        }
        if (suppressProgrammaticNextFocus) {
            onSuppressProgrammaticNextFocusConsumed?.()
            return
        }
        focusNextButton()
    }, [
        isActive,
        value.customerId,
        suppressProgrammaticNextFocus,
        onSuppressProgrammaticNextFocusConsumed,
    ])

    // Ensure programmatic customer loads (prefill / edit) get a baseline when missing.
    useEffect(() => {
        if (value.customerId == null) {
            return
        }
        if (value.contactBaseline != null) {
            return
        }
        const phone = formatPhone(value.phone ?? "")
        const label =
            value.selectedCustomerLabel != null && String(value.selectedCustomerLabel).trim() !== "" ?
                String(value.selectedCustomerLabel).trim() :
                customerDisplayLabel(value.firstName, value.lastName) || null
        const next = {
            ...value,
            phone,
            updateCustomerFields: false,
            selectedCustomerLabel: label,
        }
        next.contactBaseline = snapshotContactBaseline(next)
        onChange(next)
        // Intentionally only when customerId/baseline presence changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid loops on every keystroke
    }, [value.customerId, value.contactBaseline])

    useEffect(() => {
        if (
            !isActive ||
            activationFocusMode == null ||
            !onActivationFocusConsumed
        ) {
            return
        }
        const mode = activationFocusMode
        const tmr = window.setTimeout(() => {
            /** @type {HTMLElement | null} */
            let el = null
            if (mode === "first") {
                el = document.getElementById("contact-customer-search-query")
            } else {
                const v = valueRef.current
                if (isContactSectionComplete(v)) {
                    el = nextButtonRef.current
                } else {
                    const id = firstMissingContactFieldId(v)
                    if (id) {
                        el = document.getElementById(id)
                    }
                }
            }
            focusElement(el, { enabled: ORDER_FORM_PROGRAMMATIC_SCROLL_ENABLED, delay: true })
            onActivationFocusConsumed()
        }, activationFocusDelayMs)
        return () => window.clearTimeout(tmr)
    }, [
        isActive,
        activationFocusMode,
        activationFocusDelayMs,
        onActivationFocusConsumed,
    ])

    useEffect(() => {
        // Ensure autofilled / programmatic phone updates get formatted too.
        const raw = value.phone ?? ""
        const formatted = formatPhone(raw)
        if (formatted && formatted !== raw) {
            onChange({ ...value, phone: formatted })
        }
        // Only react to value.phone changes (avoid loops).

    }, [value.phone])

    return (
        <Accordion
            title={title}
            isActive={isOpen ?? isActive}
            isCompleted={isCompleted}
            statusLabel={statusLabel}
            disabled={disabled}
            onClick={onHeaderClick}
            hasErrors={hasErrors}
        >
            <div ref={sectionRootRef} className="row g-4">
                <div className="col-12">
                    <div className="p-3 rounded" style={{ backgroundColor: "#e7f3ff" }}>
                        <h3
                            className="fw-semibold mb-3 d-flex align-items-center"
                            style={{ color: "#1e3a8a", fontSize: "1rem" }}
                        >
                            <User className="me-2" size={20} />
                            Search Existing Customer
                        </h3>
                        <CustomerSearchInput
                            idPrefix="contact-customer-search"
                            focusOnMount
                            selectedCustomerId={/** @type {number | null} */ (value.customerId ?? null)}
                            selectedDisplayLabel={
                                value.customerId != null ?
                                    (value.selectedCustomerLabel ?? null) :
                                    null
                            }
                            onClearSelection={() => onChange(clearedContactState())}
                            onSelect={selectCustomer}
                            placeholder="Search by name, email, phone, or company..."
                        />
                    </div>
                </div>

                {showCreateCustomerAlert ? (
                    <div className="col-12">
                        <Alert variant="info" className="mb-0">
                            A customer will be created using the details below.
                        </Alert>
                    </div>
                ) : null}

                <div className="col-12">
                    <div className="row g-3">
                        <div className="col-12 col-md-6">
                            <InputField
                                id="new-order-contact-first-name"
                                label="First Name"
                                required
                                value={value.firstName}
                                labelExtra={labelHint("firstName")}
                                error={nameHasComma(value.firstName) ? NAME_COMMA_ERROR : showFieldErrors ? fieldErrors?.firstName : undefined}
                                onChange={(e) =>
                                    patchContact({ firstName: e.target.value })
                                }
                            />
                        </div>
                        <div className="col-12 col-md-6">
                            <InputField
                                id="new-order-contact-last-name"
                                label="Last Name"
                                required
                                value={value.lastName}
                                labelExtra={labelHint("lastName")}
                                error={nameHasComma(value.lastName) ? NAME_COMMA_ERROR : showFieldErrors ? fieldErrors?.lastName : undefined}
                                onChange={(e) =>
                                    patchContact({ lastName: e.target.value })
                                }
                            />
                        </div>
                        <div className="col-12">
                            <InputField
                                id="new-order-contact-email"
                                label="Email"
                                type="email"
                                required
                                value={value.email}
                                labelExtra={labelHint("email")}
                                error={showFieldErrors ? fieldErrors?.email : undefined}
                                onChange={(e) => patchContact({ email: e.target.value })}
                            />
                        </div>
                        <div className="col-12">
                            <InputField
                                id="new-order-contact-phone"
                                label="Phone"
                                type="tel"
                                required
                                value={value.phone}
                                labelExtra={labelHint("phone")}
                                error={showFieldErrors ? fieldErrors?.phone : undefined}
                                onChange={(e) =>
                                    patchContact({ phone: formatPhone(e.target.value) })
                                }
                            />
                        </div>
                        <div className="col-12">
                            <InputField
                                label="Company Name"
                                value={value.companyName}
                                labelExtra={labelHint("companyName")}
                                onChange={(e) =>
                                    patchContact({ companyName: e.target.value })
                                }
                            />
                        </div>

                        <div className="col-12 mb-3" id="new-order-contact-method">
                            <label className="form-label">
                                Preferred Contact Method
                                <span className="text-danger ms-1">*</span>
                                {labelHint("contact_method")}
                            </label>
                            <div className="d-flex gap-3">
                                {["email", "phone", "text"].map((method) => (
                                    <div key={method} className="form-check">
                                        <OrderFormRadio
                                            className={`form-check-input${showFieldErrors && fieldErrors?.contact_method ? " is-invalid" : ""}`}
                                            checked={value.contact_method === method}
                                            onSelect={() => {
                                                patchContact({ contact_method: method })
                                            }}
                                        />
                                        <label className="form-check-label text-capitalize">
                                            {method}
                                        </label>
                                    </div>
                                ))}
                            </div>
                            {showFieldErrors && fieldErrors?.contact_method ? (
                                <div className="text-danger small mt-1">{fieldErrors.contact_method}</div>
                            ) : null}
                        </div>

                        <div className="col-12 mb-3">
                            <div className="form-check">
                                <OrderFormCheckbox
                                    className="form-check-input"
                                    checked={Boolean(value.is_staff)}
                                    onCheckedChange={(checked) =>
                                        patchContact({ is_staff: Boolean(checked) })
                                    }
                                />
                                <label className="form-check-label">
                                    Is Staff?
                                    {labelHint("is_staff")}
                                </label>
                            </div>
                        </div>

                        {enablePrepaidAmount ? (
                            <div className="col-12">
                                <label
                                    className="form-label fw-semibold"
                                    style={{ fontSize: "0.875rem" }}
                                    htmlFor="contact-prepaid-amount"
                                >
                                    Prepaid Amount
                                </label>
                                <div className="input-group" style={{ maxWidth: "240px" }}>
                                    <span className="input-group-text">$</span>
                                    <input
                                        id="contact-prepaid-amount"
                                        type="number"
                                        className={`form-control${showFieldErrors && fieldErrors?.prepaidAmount ? " is-invalid" : ""}`}
                                        min="0"
                                        step="0.01"
                                        inputMode="decimal"
                                        value={String(value.prepaidAmount ?? "")}
                                        onKeyDown={(e) => {
                                            if (e.key === "-") {
                                                e.preventDefault()
                                            }
                                        }}
                                        onChange={(e) => {
                                            const raw = e.target.value
                                            if (raw === "") {
                                                patchContact({ prepaidAmount: "" })
                                                return
                                            }
                                            const n = Number(raw)
                                            patchContact({
                                                prepaidAmount: Number.isFinite(n) ?
                                                    String(Math.max(0, n)) :
                                                    "",
                                            })
                                        }}
                                    />
                                </div>
                                {showFieldErrors && fieldErrors?.prepaidAmount ? (
                                    <div className="text-danger small mt-1">{fieldErrors.prepaidAmount}</div>
                                ) : null}
                            </div>
                        ) : null}

                        <div className="col-12 col-md-6">
                            <InputField
                                label="Trip Code"
                                value={value.tripCode}
                                labelExtra={labelHint("tripCode")}
                                placeholder="Trip Code (for Lodges)"
                                onChange={(e) =>
                                    patchContact({ tripCode: e.target.value })
                                }
                            />
                        </div>
                        <div className="col-12 col-md-6">
                            <InputField
                                label="Fish Tag"
                                value={value.fishTag}
                                labelExtra={labelHint("fishTag")}
                                placeholder="Fish Tag (for Lodges)"
                                onChange={(e) =>
                                    patchContact({ fishTag: e.target.value })
                                }
                            />
                        </div>

                        <div className="col-12">
                            <InputField
                                id="new-order-contact-license"
                                label="Fishing License Number"
                                required
                                placeholder="Enter your fishing license number"
                                value={String(value.licenseNumber ?? "")}
                                labelExtra={labelHint("licenseNumber")}
                                error={showFieldErrors ? fieldErrors?.licenseNumber : undefined}
                                onChange={(e) =>
                                    patchContact({ licenseNumber: e.target.value })
                                }
                            />
                        </div>

                        <LicensePhotosUpload
                            value={value}
                            onChange={onChange}
                            frontLabelExtra={labelHint("licenseFront")}
                            backLabelExtra={labelHint("licenseBack")}
                        />
                        <div className="col-12" id="new-order-contact-license-photos">
                            {showFieldErrors && fieldErrors?.licensePhotos ? (
                                <div className="text-danger small mt-1">{fieldErrors.licensePhotos}</div>
                            ) : null}
                        </div>

                        {showUpdateCustomerPrompt ? (
                            <div className="col-12 mt-5">
                                <CustomerChoicePrompt
                                    id="contact-update-customer-fields"
                                    message="Also update the customer record to the above for future orders?"
                                    checked={Boolean(value.updateCustomerFields)}
                                    onCheckedChange={(checked) =>
                                        patchContact({ updateCustomerFields: checked })
                                    }
                                />
                                {showFieldErrors && fieldErrors?.updateCustomerFields ? (
                                    <div className="text-danger small mt-1">{fieldErrors.updateCustomerFields}</div>
                                ) : null}
                            </div>
                        ) : null}

                        <div className="col-12">
                            <div className="d-flex justify-content-end">
                                <button
                                    ref={nextButtonRef}
                                    type="button"
                                    className="btn btn-primary px-4"
                                    onClick={onNext}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Accordion>
    )
}
